import { PickupAvailability } from './product-pickup-availability'
import { ProductBlock } from './product-block'
import { ProductMedia } from './product-media'
import { Variant } from 'src/scripts/types/shopify'
import { ProductForm, ProductMediaCarousel } from '.'
import { BaseComponent } from '../../components/base-component'
import { parseHTML, replaceNodeChildren } from '../../utils/dom'
import {
  DropdownOpener,
  FloatElement,
} from 'src/scripts/components/float-element'
import { $el, $list, $elParent, whenDefined } from 'src/scripts/utils/utils'

const ELEMENTS = [
  'sticky-product-buy-btn',
  'product-buy-btn',
  'product-quantity',
]
const BLOCKS = [
  'product-price',
  'product-badges',
  'product-sku',
  'product-stock',
]
const VARIANTS_JSON_SELECTOR = '[data-variant-picker-variants]'
const OPTION_INPUTS_SELECTOR = '[data-variant-picker-option-inputs]'
const OPTION_LABELS_SELECTOR = '[data-variant-picker-option-labels]'
const DROPDOWN_LABEL_SELECTOR = '[data-dropdown-variant-picker-label]'
const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']

export class VariantPicker extends BaseComponent {
  variants: Variant[]
  variant: Variant

  constructor() {
    super()
    this.variants = JSON.parse($el(VARIANTS_JSON_SELECTOR, this).textContent)
  }

  mountComponent() {
    this.setVariant()
    this.setOptionsAvailable()
    this.setFirstMedia(true)
    this.setListeners()
  }

  handleVariantChange = event => {
    this.setVariant()
    this.setOptionsAvailable()
    this.updateLabels()
    this.updateStock()

    if (!this.variant) {
      return
    }

    this.updatePickupAvailability()
    this.updateURL()
    this.updateVariantInput()
    this.updateSection()

    if (this.dataset.productMediaFilteringOption !== event.target.name) {
      this.setFirstMedia(false)
    }

    this.emit('change', {
      variant: this.variant,
      option: event.target.name,
    })
  }

  updateLabels(): void {
    const inputContainers = $list(OPTION_INPUTS_SELECTOR, this)
    const labelContainers = $list(OPTION_LABELS_SELECTOR, this)

    inputContainers.forEach((inputContainer, idx) => {
      const labels = $list('label', labelContainers[idx])
      const inputs = $list('input', inputContainer)
      const input = this.getCheckedInput(inputs)
      const dropdownLabel = $el(DROPDOWN_LABEL_SELECTOR, labelContainers[idx])

      if (dropdownLabel) {
        dropdownLabel.toggleAttribute('no-available', !this.variant?.available)
      }

      labels.forEach(label => {
        const isSelected = label.getAttribute('for') === input.id

        if (dropdownLabel && isSelected) {
          const dropdown: DropdownOpener = $elParent(
            'dropdown-opener',
            dropdownLabel,
          )

          replaceNodeChildren(dropdownLabel, label)

          dropdown.trigger()
        }

        label.classList.toggle('selected', isSelected)
      })
    })
  }

  updateURL(): void {
    if (!this.variant || this.dataset.updateUrl === 'false') {
      return
    }

    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.variant.id}`,
    )
  }

  updateStock(): void {
    const stock = $el(
      `#product-stock-inner-${this.dataset.productId}-${this.dataset.section}`,
    )
    stock?.classList.toggle('hidden', !this.variant)
  }

  setVariant(): void {
    const options = $list(OPTION_INPUTS_SELECTOR, this)
    const selectedOptions = options.map(
      option => this.getCheckedInput($list('input', option)).value,
    )

    this.variant = this.variants.find(({ options }) =>
      options.every((option, index) => selectedOptions[index] === option),
    )

    if (!this.variant) {
      this.setUnavailable()
    }
  }

  setUnavailable() {
    this.getForm()?.setDisable(true)
    this.getForm()?.setButtonText(
      window.auroraThemeLocales.variantStrings.unavailable,
    )
  }

  setListeners(): void {
    const dropdowns: FloatElement[] = $list('float-element', this)
    const dropdownLabels = dropdowns
      .map(dropdown => $list('label', dropdown.element))
      .flat(1)
    const simpleLabels = $list('label', this)
    const labels = [...dropdownLabels, ...simpleLabels]

    labels.forEach(label =>
      this.addListener(label, 'focus', this.handleLabelFocus),
    )

    this.addListener(this, 'change', this.handleVariantChange)
  }

  handleLabelFocus = event => {
    const input = $el(`input[id="${event.target.getAttribute('for')}"]`, this)
    input.dispatchEvent(new MouseEvent('click', { button: 0, which: 1 }))
    this.addListener(document, 'keydown', this.handleLabelKeydown)
  }

  handleLabelKeydown = (event: KeyboardEvent): void => {
    const isArrowKey = ARROW_KEYS.includes(event.key)

    if (!isArrowKey) {
      return
    }

    const target = event.target as HTMLElement
    const labelContainer = $elParent(OPTION_LABELS_SELECTOR, target)

    if (!labelContainer) {
      this.removeListener(document, 'keydown', this.handleLabelKeydown)
      return
    }

    const labels = $list('label', labelContainer)
    const index = labels.findIndex(label => label === event.target)

    const currentLabel = labels[index]
    const firstLabel = labels[0]
    const lastLabel = labels[labels.length - 1]
    const nextLabel = labels[index + 1]
    const prevLabel = labels[index - 1]

    if (labels.length !== 1) {
      event.preventDefault()
    }

    const setActiveLabel = newLabel => {
      currentLabel.removeAttribute('tabindex')
      newLabel.setAttribute('tabindex', 0)
      newLabel.focus()
    }

    const changeActiveLabelOnNext = () => {
      setActiveLabel(nextLabel || firstLabel)
    }

    const changeActiveLabelOnPrev = () => {
      setActiveLabel(prevLabel || lastLabel)
    }

    const callback = {
      ArrowLeft: changeActiveLabelOnPrev,
      ArrowRight: changeActiveLabelOnNext,
      ArrowUp: changeActiveLabelOnPrev,
      ArrowDown: changeActiveLabelOnNext,
    }[event.key]

    callback()
  }

  getVariantsByOption(prevOptions) {
    const availableVariants = this.variants
      .filter(variant => variant.available)
      .map(variant => variant.options)

    if (!prevOptions) {
      return availableVariants
    }

    return availableVariants.filter(options =>
      prevOptions.every(prevOption => options.includes(prevOption.value)),
    )
  }

  async getDropdownElementBySelector(selector) {
    const dropdowns: FloatElement[] = $list('float-element', this)
    let elementBySelector

    await whenDefined('float-element').then(() => {
      elementBySelector = $el(
        selector,
        dropdowns.find(dropdown => $el(selector, dropdown.element)).element,
      )
    })

    return elementBySelector
  }

  async setOptions(options, possibleVariants) {
    for (const option of options) {
      const available = possibleVariants.some(variant =>
        variant.includes(option.value),
      )

      const labelSelector = `label[for="${option.id}"]`

      const label =
        $el(labelSelector, this) ||
        (await this.getDropdownElementBySelector(labelSelector))

      label.toggleAttribute('no-available', !available)
    }
  }

  setOptionsAvailable() {
    const options = $list(OPTION_INPUTS_SELECTOR, this)
    const firstOptions = options[0] && $list('input', options[0])
    const firstOption = this.getCheckedInput(firstOptions)
    this.setOptions(firstOptions, this.getVariantsByOption([]))

    const secondOptions = options[1] && $list('input', options[1])

    if (secondOptions) {
      const secondOption = this.getCheckedInput(secondOptions)
      this.setOptions(secondOptions, this.getVariantsByOption([firstOption]))

      const thirdOptions = options[2] && $list('input', options[2])
      if (thirdOptions) {
        this.setOptions(
          thirdOptions,
          this.getVariantsByOption([firstOption, secondOption]),
        )
      }
    }
  }

  setFirstMedia(isForceAnimation: boolean) {
    const mediaList: (ProductMediaCarousel | ProductMedia)[] = $list(
      `#product-media-${this.dataset.productId}-${this.dataset.section}`,
    )
    mediaList.forEach(media =>
      media.updateFirstMediaByVariant(this.variant, isForceAnimation),
    )
  }

  setFirstMediaByVariant(variantId: number) {
    const mediaList: (ProductMediaCarousel | ProductMedia)[] = $list(
      `#product-media-${this.dataset.productId}-${this.dataset.section}`,
    )
    mediaList.forEach(media => media.updateFirstMediaByVariant(variantId))
  }

  updateVariantInput(): void {
    const form: ProductForm = $el(
      `#product-form-component-${this.dataset.productId}-${this.dataset.section}`,
    )

    if (!form) {
      return
    }

    form.getVeriantInput().setAttribute('value', String(this.variant.id))
    form.getVeriantInput().dispatchEvent(new Event('change', { bubbles: true }))
  }

  updatePickupAvailability(): void {
    const pickupAvailability: PickupAvailability = $el(
      `#product-pickup-availability-${this.dataset.productId}-${this.dataset.section}`,
    )
    if (pickupAvailability) {
      pickupAvailability.updateVariantId(this.variant.id)
      pickupAvailability.fetchAvailability()
    }
  }

  updateSection(): void {
    const form: ProductForm = $el(
      `#product-form-component-${this.dataset.productId}-${this.dataset.section}`,
    )

    this.updateBlocks()

    form?.setDisable(true)
    form?.setLoading(true)

    fetch(
      `${this.dataset.url}?variant=${this.variant.id}&section_id=${this.dataset.sectionIdWithoutPrefix}`,
    )
      .then(response => response.text())
      .then(responseText => {
        form?.setDisable(!this.variant.available)
        form?.setLoading(false)
        this.updateElements(parseHTML(responseText))
      })
      .catch(() => {
        console.log('Error in variant picker component')
      })
  }

  updateElements(html): void {
    ELEMENTS.forEach(block => {
      const id = `${block}-${this.dataset.productId}-${this.dataset.section}`
      const target = $el(`#${id}`)
      const newTarget = $el(`#${id}`, html)
      replaceNodeChildren(target, newTarget)
    })
  }

  updateBlocks(): void {
    BLOCKS.forEach(blockId => {
      const block: ProductBlock = $el(
        `#${blockId}-${this.dataset.productId}-${this.dataset.section}`,
      )
      block?.updateByVariantId(this.variant.id)
    })
  }

  getCheckedInput(inputs): HTMLInputElement {
    return inputs.find(({ checked }) => checked)
  }

  getForm(): ProductForm {
    return $el(
      `#product-form-component-${this.dataset.productId}-${this.dataset.section}`,
    )
  }
}
